/**
 * EditorDomanda — una domanda del questionario: tipo, testi, obbligo, opzioni, campo che compare
 * sotto un'opzione, limiti dei numeri, salto condizionale. Tutto come DATO: il motore non sa di
 * che cosa parla la domanda.
 */

import React from 'react';
import {
  Box, Checkbox, Chip, FormControlLabel, IconButton, MenuItem, Paper, Stack, Switch,
  TextField, Tooltip, Typography,
} from '@mui/material';
import { ArrowDownward, ArrowUpward, DeleteOutline, LockOutlined } from '@mui/icons-material';
import { TIPI_CON_OPZIONI, TIPI_DOMANDA, type TipoDomanda } from '@shared/questionari';
import { tokens } from '../../../theme';
import { TIPI_CON_SALTO, TIPO_DOMANDA_LABELS } from '../labels';
import { CampoTesto, interoDa } from './CampoTesto';
import { EditorNumero } from './EditorNumero';
import { EditorOpzioni } from './EditorOpzioni';
import { cambiaTipo, codiceUnico, type BozzaDomanda } from './bozza';

interface Props {
  domanda: BozzaDomanda;
  indice: number;
  totale: number;
  /** Tutte le domande: servono per i codici unici e per scegliere dove saltare. */
  tutte: BozzaDomanda[];
  codiceBloccato: boolean;
  opzioniBloccate: Set<string>;
  onChange: (domanda: BozzaDomanda) => void;
  onSu: () => void;
  onGiu: () => void;
  onRimuovi: () => void;
}

export const EditorDomanda: React.FC<Props> = ({
  domanda, indice, totale, tutte, codiceBloccato, opzioniBloccate, onChange, onSu, onGiu, onRimuovi,
}) => {
  const aggiorna = (patch: Partial<BozzaDomanda>) => onChange({ ...domanda, ...patch });
  const altriCodici = tutte.filter((d) => d.chiave !== domanda.chiave).map((d) => d.id);

  /** Finché il codice non è scritto a mano, segue il nome breve (o il testo). */
  const conCodiceProposto = (patch: Partial<BozzaDomanda>): Partial<BozzaDomanda> => {
    if (domanda.idFisso || codiceBloccato) return patch;
    const base = (patch.nome_breve ?? domanda.nome_breve ?? '').trim() || (patch.testo ?? domanda.testo).trim();
    return base ? { ...patch, id: codiceUnico(base, altriCodici) } : patch;
  };

  const conOpzioni = TIPI_CON_OPZIONI.includes(domanda.tipo);
  const multipla = domanda.tipo === 'scelta_multipla';
  const conSalto = TIPI_CON_SALTO.includes(domanda.tipo);
  const valoriSalto = domanda.tipo === 'si_no'
    ? [{ id: 'si', etichetta: 'sì' }, { id: 'no', etichetta: 'no' }]
    : domanda.opzioni.filter((o) => o.id).map((o) => ({ id: o.id, etichetta: o.etichetta || o.id }));
  const domandeDopo = tutte.slice(indice + 1);

  return (
    <Paper variant="outlined" sx={{ p: 2, borderColor: tokens.color.line }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 700, color: tokens.color.graphite }}>
          Domanda {indice + 1}
        </Typography>
        <TextField
          size="small"
          value={domanda.id}
          onChange={(e) => aggiorna({ id: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'), idFisso: true })}
          disabled={codiceBloccato}
          placeholder="codice"
          inputProps={{ 'aria-label': 'Codice della domanda' }}
          InputProps={{
            sx: { fontFamily: tokens.font.mono, fontSize: '0.78rem', height: 30 },
            endAdornment: codiceBloccato ? (
              <Tooltip title="Ha già risposte: il codice non si cambia">
                <LockOutlined fontSize="small" sx={{ color: tokens.color.mist }} />
              </Tooltip>
            ) : undefined,
          }}
          sx={{ width: 220 }}
        />
        <Box sx={{ flex: 1 }} />
        <IconButton size="small" onClick={onSu} disabled={indice === 0} aria-label="Sposta su"><ArrowUpward fontSize="small" /></IconButton>
        <IconButton size="small" onClick={onGiu} disabled={indice === totale - 1} aria-label="Sposta giù"><ArrowDownward fontSize="small" /></IconButton>
        <Tooltip title="Rimuovi domanda">
          <IconButton size="small" sx={{ color: tokens.color.danger }} onClick={onRimuovi} aria-label="Rimuovi domanda">
            <DeleteOutline fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>

      <Stack spacing={1.5}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <TextField
            select
            size="small"
            label="Tipo"
            value={domanda.tipo}
            onChange={(e) => onChange(cambiaTipo(domanda, e.target.value as TipoDomanda))}
            sx={{ minWidth: { sm: 190 } }}
          >
            {TIPI_DOMANDA.map((t) => <MenuItem key={t} value={t}>{TIPO_DOMANDA_LABELS[t]}</MenuItem>)}
          </TextField>
          <CampoTesto
            label="Testo della domanda"
            value={domanda.testo}
            onChange={(v) => aggiorna(conCodiceProposto({ testo: v }))}
            multiline
            maxRows={3}
          />
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <CampoTesto
            label="Nome breve"
            value={domanda.nome_breve}
            onChange={(v) => aggiorna(conCodiceProposto({ nome_breve: v }))}
            helperText="Per statistiche, esportazione e riepilogo nell'email (es. «Strumenti più utili»)"
          />
          <CampoTesto
            label="Riga di aiuto"
            value={domanda.aiuto}
            onChange={(v) => aggiorna({ aiuto: v })}
            helperText="Sotto la domanda (es. «Puoi scegliere più risposte»)"
          />
        </Stack>

        {(domanda.tipo === 'testo_breve' || domanda.tipo === 'testo_lungo') && (
          <CampoTesto label="Esempio nel campo" value={domanda.segnaposto} onChange={(v) => aggiorna({ segnaposto: v })} />
        )}

        {conOpzioni && (
          <EditorOpzioni
            opzioni={domanda.opzioni}
            multipla={multipla}
            bloccati={opzioniBloccate}
            onChange={(opzioni) => aggiorna(conRiferimentiAggiornati(domanda, opzioni))}
          />
        )}

        {multipla && (
          <CampoTesto
            label="Massimo di scelte"
            value={domanda.massimo_scelte === undefined ? '' : String(domanda.massimo_scelte)}
            onChange={(v) => aggiorna({ massimo_scelte: interoDa(v) })}
            helperText="Vuoto = senza limite. Con un limite si capiscono le priorità vere"
            inputProps={{ inputMode: 'numeric' }}
            sx={{ maxWidth: 260 }}
          />
        )}

        {conOpzioni && <EditorCampoDettaglio domanda={domanda} onChange={aggiorna} />}

        {domanda.tipo === 'numero' && <EditorNumero domanda={domanda} onChange={aggiorna} />}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
          <FormControlLabel
            control={<Checkbox size="small" checked={domanda.obbligatoria} onChange={(e) => aggiorna({ obbligatoria: e.target.checked })} />}
            label={<Typography variant="body2">Obbligatoria</Typography>}
            sx={{ mr: 'auto' }}
          />
          {conSalto && (
            <>
              <TextField
                select
                size="small"
                label="Se la risposta è"
                value={domanda.salta_a?.se_risposta ?? ''}
                onChange={(e) => aggiorna({
                  salta_a: e.target.value
                    ? { se_risposta: e.target.value, vai_a_domanda_id: domanda.salta_a?.vai_a_domanda_id ?? '' }
                    : undefined,
                })}
                sx={{ minWidth: 180 }}
              >
                <MenuItem value=""><em>nessun salto</em></MenuItem>
                {valoriSalto.map((v) => <MenuItem key={v.id} value={v.id}>{v.etichetta}</MenuItem>)}
              </TextField>
              <TextField
                select
                size="small"
                label="Vai alla domanda"
                value={domanda.salta_a?.vai_a_domanda_id ?? ''}
                onChange={(e) => domanda.salta_a && aggiorna({ salta_a: { ...domanda.salta_a, vai_a_domanda_id: e.target.value } })}
                disabled={!domanda.salta_a}
                helperText="Solo in avanti"
                sx={{ minWidth: 220 }}
              >
                <MenuItem value=""><em>scegli…</em></MenuItem>
                {domandeDopo.map((d) => {
                  const n = tutte.findIndex((x) => x.chiave === d.chiave) + 1;
                  const t = d.testo.trim();
                  return (
                    <MenuItem key={d.chiave} value={d.id}>
                      {t ? `${n}. ${t.slice(0, 40)}${t.length > 40 ? '…' : ''}` : `${n}. (senza testo)`}
                    </MenuItem>
                  );
                })}
              </TextField>
            </>
          )}
        </Stack>
      </Stack>
    </Paper>
  );
};

/**
 * Le opzioni nuove, più i riferimenti che le nominano per codice (il campo che compare, il salto)
 * aggiornati se un codice è cambiato o tolti se l'opzione non c'è più.
 */
function conRiferimentiAggiornati(domanda: BozzaDomanda, opzioni: BozzaDomanda['opzioni']): Partial<BozzaDomanda> {
  const nuovoCodice = new Map<string, string | null>();
  domanda.opzioni.forEach((vecchia) => {
    const nuova = opzioni.find((o) => o.chiave === vecchia.chiave);
    if (!nuova) nuovoCodice.set(vecchia.id, null);
    else if (nuova.id !== vecchia.id) nuovoCodice.set(vecchia.id, nuova.id);
  });
  const patch: Partial<BozzaDomanda> = { opzioni };
  if (nuovoCodice.size === 0) return patch;
  const rimappa = (id: string): string | null => (nuovoCodice.has(id) ? nuovoCodice.get(id) ?? null : id);
  if (domanda.campo_dettaglio) {
    patch.campo_dettaglio = {
      ...domanda.campo_dettaglio,
      mostra_se: domanda.campo_dettaglio.mostra_se.map(rimappa).filter((x): x is string => !!x),
    };
  }
  if (domanda.salta_a && domanda.tipo === 'scelta_singola') {
    const se = rimappa(domanda.salta_a.se_risposta);
    patch.salta_a = se ? { ...domanda.salta_a, se_risposta: se } : undefined;
  }
  return patch;
}

/** Il campo di testo che compare sotto le opzioni scelte («Altro» → «Quali?»). */
const EditorCampoDettaglio: React.FC<{
  domanda: BozzaDomanda;
  onChange: (patch: Partial<BozzaDomanda>) => void;
}> = ({ domanda, onChange }) => {
  const cd = domanda.campo_dettaglio;
  const opzioni = domanda.opzioni.filter((o) => o.id);
  return (
    <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: tokens.color.bone, border: `1px solid ${tokens.color.lineSoft}` }}>
      <FormControlLabel
        control={(
          <Switch
            size="small"
            checked={!!cd}
            onChange={(e) => onChange({
              campo_dettaglio: e.target.checked ? { etichetta: '', mostra_se: [] } : undefined,
            })}
          />
        )}
        label={<Typography variant="body2">Campo di testo che compare sotto certe opzioni</Typography>}
      />
      {cd && (
        <Stack spacing={1.25} sx={{ mt: 1 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <CampoTesto label="Etichetta del campo" value={cd.etichetta} onChange={(v) => onChange({ campo_dettaglio: { ...cd, etichetta: v } })} />
            <CampoTesto label="Esempio nel campo" value={cd.segnaposto} onChange={(v) => onChange({ campo_dettaglio: { ...cd, segnaposto: v } })} />
          </Stack>
          <Box>
            <Typography variant="caption" sx={{ color: tokens.color.ash }}>Compare con queste opzioni:</Typography>
            <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', rowGap: 0.75, mt: 0.5 }}>
              {opzioni.map((o) => {
                const scelta = cd.mostra_se.includes(o.id);
                return (
                  <Chip
                    key={o.chiave}
                    size="small"
                    label={o.etichetta || o.id}
                    color={scelta ? 'primary' : 'default'}
                    variant={scelta ? 'filled' : 'outlined'}
                    onClick={() => onChange({
                      campo_dettaglio: {
                        ...cd,
                        mostra_se: scelta ? cd.mostra_se.filter((x) => x !== o.id) : [...cd.mostra_se, o.id],
                      },
                    })}
                  />
                );
              })}
              {opzioni.length === 0 && <Typography variant="caption" sx={{ color: tokens.color.mist }}>Prima scrivi le opzioni.</Typography>}
            </Stack>
          </Box>
          <FormControlLabel
            control={<Checkbox size="small" checked={cd.obbligatorio === true} onChange={(e) => onChange({ campo_dettaglio: { ...cd, obbligatorio: e.target.checked || undefined } })} />}
            label={<Typography variant="body2">Obbligatorio quando compare</Typography>}
          />
        </Stack>
      )}
    </Box>
  );
};
